/**
 * The panel's building blocks. Each carries a rule the prototype learned the
 * hard way, so a view cannot forget it:
 *  - Field: every label names its control (A11Y-ADM-01 — the audit found 33
 *    unnamed fields); the id comes from useId, never typed by hand.
 *  - Msg: one role="status" line, so a result is announced.
 *  - TwoStep: a destructive action is two clicks on the same button, never a
 *    browser dialog (which blocks the page and which staff click through).
 *  - DropZone: a <label> around a visually hidden file input — the whole box
 *    opens the chooser, Tab reaches it, and drag-and-drop works too.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import type { Tone } from '../lib/hooks';
import { fieldErrorToast } from '../lib/toast';
import { CAPABILITY_OFF_NO } from '../lib/capability-off';
import markSvg from '../../../../brand/logo-mark.svg?raw';

/**
 * A card. `hint` is ONE short sentence under the title; anything longer goes in `more`, behind «Les mer» (a native
 * <details>, so it is a real button for the keyboard and a screen reader, and closed until somebody asks).
 */
export function Card({ title, hint, more, children, id, className = '', titleId }: {
  title?: React.ReactNode; hint?: React.ReactNode; more?: React.ReactNode; children?: React.ReactNode; id?: string; className?: string; titleId?: string;
}) {
  return (
    <div className={'card ' + className} id={id}>
      {title !== undefined && <h3 id={titleId}>{title}</h3>}
      {hint !== undefined && <p className="hint">{hint}</p>}
      {more !== undefined && <More>{more}</More>}
      {children}
    </div>
  );
}

/** «Les mer»: the rest of an explanation, folded. */
export const More = ({ children, label = 'Les mer' }: { children: React.ReactNode; label?: string }) =>
  <details className="more"><summary>{label}</summary><div className="more-body">{children}</div></details>;

export const Body = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) =>
  <div className="card-body" style={style}>{children}</div>;

export type TagTone = 'green' | 'gold' | 'grey' | 'red';
export const Tag = ({ tone, children, title }: { tone: TagTone; children: React.ReactNode; title?: string }) =>
  <span className={'tag ' + tone} title={title}>{children}</span>;

/**
 * The message line. Since 2026-10-31 (owner: «convert all success messages and errors and validations into toast
 * messages») the words a view says through `useMsg()` go up as a toast (lib/toast.ts, components/Toaster.tsx), seen
 * wherever the page is scrolled — so this draws nothing. It stays so the views keep their place for it.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function Msg(_props: { text: string; tone?: Tone; id?: string; style?: React.CSSProperties }) {
  return null;
}

/** A field's own refusal, right under the field (not only in the line by «Lagre»). Empty: nothing. */
export function FieldError({ text, id }: { text?: string | null; id?: string }) {
  // Also a toast (2026-10-31), so a check that failed far down the form is seen — once, not once per field.
  useEffect(() => { if (text) fieldErrorToast(text); }, [text]);
  return text ? <span className="msg err field-err" id={id} role="alert">{text}</span> : null;
}

/**
 * `error`: the server's (or core's) words for this field, shown right under its control, named by aria-describedby,
 * and the control outlined red (CSS: .field.has-err) — the field to fix is the one that says so.
 */
export function Field({ label, required, note, children, className = 'field', style, noteId, error }: {
  label: React.ReactNode; required?: boolean; note?: React.ReactNode;
  children: (id: string, describedBy?: string) => React.ReactNode;
  className?: string; style?: React.CSSProperties; noteId?: string; error?: string | null;
}) {
  const id = useId();
  const nid = note ? (noteId || id + '-note') : undefined;
  const eid = error ? id + '-err' : undefined;
  return (
    <div className={className + (error ? ' has-err' : '')} style={style}>
      <label htmlFor={id}>{label}{required && <> <i>*</i></>}</label>
      {children(id, [eid, nid].filter(Boolean).join(' ') || undefined)}
      <FieldError text={error} id={eid} />
      {note && <span className="note" id={nid}>{note}</span>}
    </div>
  );
}

/**
 * Polish pass (2026-10-30): a button whose onClick is async is disabled (and aria-busy) until that promise settles.
 * About twenty buttons — «Legg til ansatt», «Ny skjerm», «Lagre» on a partner, a new event type — posted once per click,
 * so a second click on a slow line made a second row. The buttons that keep their own busy state are unchanged.
 */
export function Btn({ children, ghost, danger, small = true, onClick, ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  ghost?: boolean; danger?: boolean; small?: boolean;
}) {
  const [running, setRunning] = useState(false);
  const live = useRef(true);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  const cls = ['btn', small ? 'sm' : '', ghost ? 'ghost' : '', danger ? 'danger' : '', rest.className || ''].filter(Boolean).join(' ');
  const click = onClick && ((e: React.MouseEvent<HTMLButtonElement>) => {
    if (running) return;
    const out = onClick(e) as unknown;
    if (out && typeof (out as Promise<unknown>).then === 'function') {
      setRunning(true);
      // finally, not catch: a rejection is still reported as it was before.
      void Promise.resolve(out).finally(() => { if (live.current) setRunning(false); });
    }
  });
  // aria-disabled, not disabled, while it runs: a disabled button drops the keyboard focus to the page's top.
  return <button type="button" {...rest} onClick={click} aria-disabled={running || rest['aria-disabled'] || undefined}
    aria-busy={rest['aria-busy'] ?? (running || undefined)} className={cls}>{children}</button>;
}

/**
 * Masjid UX redesign (2026-10-23, ADM-01 / ADM-14): a DOUBLE-click is never a confirmation — a second click less than
 * 600 ms after arming is ignored, so the habit of double-clicking cannot capture money or end a membership. And an armed
 * button no longer gives up by itself after 6 s (a time limit slow readers lose to, WCAG 2.2.1): it stays armed until
 * it is clicked again, Escape is pressed, or focus leaves it.
 *
 * First click arms it (the label says what the second will do); a second click does it. While the action
 * runs the button is disabled and says so (`busyLabel`) — a third click on a slow network is not a second janazah, a
 * second capture or a second import (QA 2026-10-16, A12/A13).
 *
 * UX audit U-04 (2026-10-19), for a form with more than one of these (approve / reject a membership):
 *  - `check` runs before arming. When it returns false the button is NOT armed — the caller has said what is missing —
 *    so a double click on «Godkjenn» with the person or the method empty is not an armed approval and an error.
 *  - `armed` + `onArmedChange` let the form arm one at a time: arming «Avslå» disarms «Godkjenn».
 *  - `go`: an approval stays green when armed (with a ring to show it is armed). Red is for what takes away.
 */
/** A second click sooner than this after arming is the tail of a double-click, not a decision. */
export const DOUBLE_CLICK_MS = 600;

export function TwoStep({ label, confirmLabel, onConfirm, ghost = true, disabled, busyLabel = 'Vent …', ariaLabel, check, armed: armedProp, onArmedChange, go }: {
  label: string; confirmLabel: string; onConfirm: () => void | Promise<void>; ghost?: boolean; disabled?: boolean; busyLabel?: string;
  /** What the button does, for a screen reader, when `label` alone does not say which row it is on. */
  ariaLabel?: string;
  check?: () => boolean; armed?: boolean; onArmedChange?: (armed: boolean) => void; go?: boolean;
}) {
  const [own, setOwn] = useState(false);
  const armed = armedProp ?? own;
  const setArmed = (v: boolean) => { setOwn(v); onArmedChange?.(v); };
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState('');
  const armedAt = useRef(0);
  const disarm = () => { if (armed) { setArmed(false); setSaid('Avbrutt.'); } };
  // Disarmed from outside (the other button of the pair was armed): the live region stops saying it is armed.
  useEffect(() => { if (armedProp === false) { setOwn(false); setSaid((s) => (s === confirmLabel ? '' : s)); } }, [armedProp, confirmLabel]);
  // The label change alone is not reliably read aloud: a live region beside the button says it was armed, and that it
  // was cancelled (store iteration 4, review P3).
  return (
    <>
      <Btn ghost={!armed && ghost} danger={armed && !go} className={armed && go ? 'armed' : undefined} disabled={disabled || busy} aria-busy={busy || undefined} aria-label={!armed && !busy ? ariaLabel : undefined}
           onKeyDown={(e) => { if (e.key === 'Escape') disarm(); }}
           onBlur={() => { if (!busy) disarm(); }}
           onClick={async () => {
             if (busy) return;
             if (!armed) { if (check && !check()) return; setArmed(true); armedAt.current = Date.now(); setSaid(confirmLabel); return; }
             if (Date.now() - armedAt.current < DOUBLE_CLICK_MS) return;   // the second half of a double-click
             setArmed(false); setSaid(''); setBusy(true);
             try { await onConfirm(); } finally { setBusy(false); }
           }}>
        {busy ? busyLabel : armed ? confirmLabel : label}
      </Btn>
      <span className="vh" role="status">{said}</span>
    </>
  );
}

export function DropZone({ title, children, accept, onFile, inputRef }: {
  title: string; children: React.ReactNode; accept: string; onFile: (f: File) => void;
  inputRef?: React.RefObject<HTMLInputElement>;
}) {
  const [over, setOver] = useState(false);
  const own = useRef<HTMLInputElement>(null);
  const ref = inputRef || own;
  return (
    <label className={'drop' + (over ? ' over' : '')}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); const f = e.dataTransfer.files[0]; if (f) onFile(f); }}>
      <b>{title}</b>{children}
      <input ref={ref} type="file" className="vh" accept={accept}
             onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) onFile(f); }} />
    </label>
  );
}

export const Loading = ({ what = 'Henter …' }: { what?: string }) => <div className="loading" role="status">{what}</div>;

/**
 * A view whose endpoint failed says so, in its own card, and the rest of the panel carries on. A function that is switched
 * off (useLoad's CAPABILITY_OFF_NO, PM gaps B1) is not a failure: it says so and who can switch it on, and offers no
 * «Prøv igjen» — trying again changes nothing.
 */
function CapabilityOff({ text }: { text: string }) {
  return (
    <div className="card"><div className="card-body">
      <p className="msg" role="status" style={{ marginTop: 0 }}>{text} Under Funksjoner står det hva den venter på; spør en administrator.</p>
    </div></div>
  );
}
export const LoadError = ({ error, retry }: { error: string; retry?: () => void }) => (error === CAPABILITY_OFF_NO ? <CapabilityOff text={error} /> : (
  <div className="card"><div className="card-body">
    {/* role="alert" (UX audit U-09): a failed load is said, as Conflict and MoreButton already are. */}
    <p className="msg err" role="alert" style={{ marginTop: 0 }}>Kunne ikke hente dette: {error}</p>
    {retry && <Btn ghost onClick={retry}>Prøv igjen</Btn>}
  </div></div>
));

/** The mosque's own mark, from brand/logo-mark.svg — painted with currentColor. */
export function Mark({ width = 27, height = 38 }: { width?: number; height?: number }) {
  const svg = markSvg.replace(/<\?xml[^>]*>/, '').replace(/<!--[\s\S]*?-->/g, '')
    .replace('<svg', `<svg aria-hidden="true" focusable="false" width="${width}" height="${height}"`);
  return <span style={{ display: 'inline-flex', lineHeight: 0 }} dangerouslySetInnerHTML={{ __html: svg }} />;
}

/** Copy a link, or select it when the browser has no clipboard (an http page). */
/**
 * Copy one value, from wherever it is shown.  Daniyal, 2026-09-20.
 *
 * The TV address of a screen was copyable for exactly as long as the page
 * remembered having just created it: the button lived inside the "new screen"
 * block, so coming back the next day — the day you actually need it, because
 * the TV has been wiped or replaced — left the address as plain text to select
 * by hand. A value worth showing is worth copying.
 *
 * `navigator.clipboard` needs a secure context and a user gesture, and is not
 * there at all in some embedded browsers. The fallback selects the text and
 * says which keys to press, rather than failing silently — a copy button that
 * does nothing is worse than no copy button.
 */
export function CopyButton({ value, label = 'Kopier', done = 'Kopiert', selectRef, onMessage }: {
  value: string;
  label?: string;
  done?: string;
  /** The node holding the same text, selected when the clipboard is refused. */
  selectRef?: React.RefObject<HTMLElement | null>;
  onMessage?: (m: string) => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <Btn ghost onClick={async () => {
      try {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        onMessage?.('Adressen er kopiert.');
        setTimeout(() => setCopied(false), 4000);
      } catch {
        const node = selectRef?.current;
        if (node) {
          const range = document.createRange();
          range.selectNodeContents(node);
          const sel = getSelection();
          if (sel) { sel.removeAllRanges(); sel.addRange(range); }
        }
        onMessage?.('Adressen er merket — trykk Ctrl+C (Cmd+C på Mac) for å kopiere.');
      }
    }}>{copied ? done : label}</Btn>
  );
}

export function CopyField({ label, value }: { label: string; value: string }) {
  const [done, setDone] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div className="row">
        <input id={id} ref={ref} type="text" readOnly value={value} style={{ flex: 1 }} />
        <Btn ghost onClick={async () => {
          try { await navigator.clipboard.writeText(value); setDone(true); }
          catch { ref.current?.select(); }
        }}>{done ? 'Kopiert' : 'Kopier'}</Btn>
      </div>
    </div>
  );
}
